import type {
  GreatKingdomCell,
  GreatKingdomPlayer,
  GreatKingdomPlayerId,
  GreatKingdomState,
  InitGameOptions,
} from "./types";
import {
  GREAT_KINGDOM_BOARD_SIZE,
  GREAT_KINGDOM_CENTER,
  GREAT_KINGDOM_PLAYERS,
} from "./types";
import { calculateTerritory, setCell } from "./utils";

export function createEmptyBoard(): readonly (readonly GreatKingdomCell[])[] {
  return Array.from({ length: GREAT_KINGDOM_BOARD_SIZE }, () =>
    Array.from({ length: GREAT_KINGDOM_BOARD_SIZE }, () => "empty" as const),
  );
}

function createPlayer(playerId: GreatKingdomPlayerId): GreatKingdomPlayer {
  return {
    playerId,
    label: playerId === "p1" ? "Blue Kingdom" : "Orange Kingdom",
    color: playerId === "p1" ? "blue" : "orange",
    scoreEstimate: 0,
  };
}

function assertPlayerId(playerId: GreatKingdomPlayerId, context: string): void {
  if (!GREAT_KINGDOM_PLAYERS.includes(playerId)) {
    throw new Error(`${context} must be p1 or p2`);
  }
}

export function initGame(options: InitGameOptions): GreatKingdomState {
  assertPlayerId(options.hostPlayerId, "Host player");
  if (options.firstPlayerId) {
    assertPlayerId(options.firstPlayerId, "First player");
  }

  const setupMode = options.setupMode ?? "basic";
  const firstPlayerId = options.firstPlayerId ?? options.hostPlayerId;
  const board =
    setupMode === "basic"
      ? setCell(
          createEmptyBoard(),
          GREAT_KINGDOM_CENTER.row,
          GREAT_KINGDOM_CENTER.col,
          "neutral",
        )
      : createEmptyBoard();
  const territory = calculateTerritory(board).summary;

  return {
    gameId:
      options.gameId ?? `great-kingdom-${GREAT_KINGDOM_PLAYERS.join("-")}`,
    hostPlayerId: options.hostPlayerId,
    players: GREAT_KINGDOM_PLAYERS.map((playerId) => ({
      ...createPlayer(playerId),
      scoreEstimate: territory[playerId],
    })),
    setupMode,
    phase: setupMode === "advanced" ? "neutral-setup" : "playing",
    board,
    currentPlayerId: firstPlayerId,
    moveNumber: 0,
    passStreak: 0,
    territory,
    capturedGroups: [],
    winnerId: null,
    endReason: null,
    lastAction: null,
  };
}

export function initBasicGame(
  firstPlayerId: GreatKingdomPlayerId = "p1",
): GreatKingdomState {
  return initGame({
    hostPlayerId: firstPlayerId,
    firstPlayerId,
    setupMode: "basic",
  });
}

export function initAdvancedGame(
  firstPlayerId: GreatKingdomPlayerId = "p1",
): GreatKingdomState {
  return initGame({
    hostPlayerId: firstPlayerId,
    firstPlayerId,
    setupMode: "advanced",
  });
}
